import * as d3 from "d3";
import coastJsonUrl from "../wm_sm.json";
import * as L from "leaflet/src/Leaflet";
import Locate from "leaflet.locatecontrol";
import {debounce} from "./utils";
import {layout, layers, map} from "./common";
import {updatePositions, displayForecastDetails, updateParticlesBBox} from "./data";
import {config} from "./config";
import {patchLeafletBounds} from "./patched-leaflet-bounds";
import {refreshDataLayers} from "./timeline";

export function initMap() {
  map.instance = L.map('map', {
    center: config.mapCenter,
    zoom: 9.5,
    minZoom: 9.3,
    maxBoundsViscosity: 1,
    attributionControl: false,
    zoomControl: false,
    zoomSnap: 0,
    zoomAnimation: false,
    //noMoveStart:true,
    renderer: L.canvas()
  });

  L.control.zoom({position: 'topright'}).addTo(map.instance);
  new Locate({
    position: 'bottomright',
    keepCurrentZoomLevel: true,
    setView: false,
    strings: {
      title: "Voir ma position",
      metersUnit: "mètres",
      feetUnit: "pieds",
      popup: "Vous êtes situé(e) dans un périmètre de {distance} {unit} autour de ce point",
      outsideMapBoundsMsg: "Vous semblez être localisé(e) hors du périmètre de la carte"
    },
    locateOptions: {
      enableHighAccuracy: true
    }
  }).addTo(map.instance);
  patchLeafletBounds(L.Bounds);

  L.svg().addTo(map.instance);
  document.querySelector("#map .leaflet-overlay-pane")
    .insertAdjacentHTML('afterbegin', '<div id="particles_wrapper" class="leaflet-zoom-animated" style="width: ' + layout.width + 'px; height: ' + layout.height + 'px;"></div>');

  let isSingleClick = true, updateParticles = debounce(updateParticlesBBox, 500);
  map.instance.on("zoomstart", function() {
    stopParticles();
  });
  map.instance.on("zoomend", function(evt) {
    updatePositions();
    setTimeout(function() {
      isSingleClick = true;
    }, 300);
    if ((map.windLayer && layers.dataLayers.indexOf('wind_fc') !== -1) ||
      (map.wavesLayer && layers.dataLayers.indexOf('waves_fc') !== -1)) {
      updateParticles();
    }
  });
  map.instance.on("movestart", function() {
    stopParticles();
  });
  map.instance.on("moveend", function(evt) {
    updatePositions();
    if ((map.windLayer && layers.dataLayers.indexOf('wind_fc') !== -1) ||
      (map.wavesLayer && layers.dataLayers.indexOf('waves_fc') !== -1)) {
      updateParticles();
    }
    updateParticlesPane();
  });
  map.instance.on('click', function (e) {
    setTimeout(function() {
      if (isSingleClick) {
        if (layers.dataLayers.indexOf('wind_fc') !== -1 || layers.dataLayers.indexOf('waves_fc') !== -1) {
          let popLocation = e.latlng;
          map.popup = L.popup().setLatLng(popLocation);
          config.popup = map.popup;
          setPopupContent(config.popup, e);
          map.instance.openPopup(config.popup);
        }
      }
    }, 300);
  });
  map.instance.on('dblclick', function(e) {
    isSingleClick = false;
  });
  map.instance.on('popupclose', function(e) {
    map.popup = null;
  });
  map.instance.on('locationerror', function (e) {
    window.alert("Votre position n'a pas pu être relevée.")
  });

  layers.overlay = d3.select(map.instance.getPanes().overlayPane).select("svg").attr("id", "markers_layer")
    .attr("pointer-events", "all");

  return displayCoastLine().then(function(geoJson) {
    let maxBounds = geoJson.getBounds();
    map.instance.fitBounds(maxBounds, {padding: [-200, -80]}).setMaxBounds(maxBounds);
    map.viewBox = map.instance.getPixelBounds();
    map.refZoom = map.instance.getBoundsZoom([[49, -5], [45.96, -1.05]]);
    map.windBox = [map.instance.project([49, -5], map.refZoom), map.instance.project([45.96, -1.05], map.refZoom)];
    map.windWidth = map.windBox[1].x - map.windBox[0].x;
    map.windHeight = map.windBox[1].y - map.windBox[0].y;
    map.wavesBox = [map.instance.project([49, -5], map.refZoom), map.instance.project([45.96, -0.98], map.refZoom)];
    map.wavesWidth = map.wavesBox[1].x - map.wavesBox[0].x;
    map.wavesHeight = map.wavesBox[1].y - map.wavesBox[0].y;
    // console.log('map.windBox : ' + map.windBox + ' - map.windWidth : ' + map.windWidth + ' - map.windHeight : ' + map.windHeight + ' - map.refZoom : ' + map.refZoom);
    // console.log('map.viewBox : ' + JSON.stringify(map.viewBox));
  });
}

function updateParticlesPane() {
  let panTransform = document.querySelector("#map > .leaflet-map-pane").getAttribute('style');
  if (panTransform && panTransform.indexOf('translate3d') !== -1) {
    let [x, y, z] = panTransform.match(/-?[.\d]+px/g).map(function(v) { return parseInt(v.replace('px', '')); });
    document.querySelector("#particles_wrapper").setAttribute('style',
      'width: ' + layout.width + 'px; height: ' + layout.height + 'px; transform: translate3d(' + (-x) + 'px, ' + (-y) + 'px, 0px);');
    if (document.querySelector("#waves_wrapper")) {
      document.querySelector("#waves_wrapper").setAttribute('style',
        'width: ' + layout.width + 'px; height: ' + layout.height + 'px; transform: translate3d(' + (-x) + 'px, ' + (-y) + 'px, 0px);');
    }
  }
}

function stopParticles() {
  if (map.windLayer && layers.dataLayers.indexOf('wind_fc') !== -1) {
    map.windLayer.stop();
  }

  if (map.wavesLayer && layers.dataLayers.indexOf('waves_fc') !== -1) {
    map.wavesLayer.stop();
  }
}

export function toggleMap(btn, ref) {
  if (map.activeMap !== ref) {
    let activeBtn = document.querySelector("#map_bg_list button.active");
    if (activeBtn) {
      activeBtn.classList.remove('active');
    }
    displayMapRef(ref);
    map.activeMap = ref;
    btn.classList.add('active');
  }
}

export function displayMapRef(ref, skipRefresh) {
  console.debug('displaying map ' + ref);
  let mapContainer = document.querySelector("#map_container");
  if (ref !== 'elevation') {
    clearBgLayers();
  }
  if (ref === 'wm') {
    displayCoastLine(true);
  } else if (ref === 'satellite') {
    displayOrthoLayer();
  } else if (ref === 'elevation') {
    displayMntLayer();
  }
  mapContainer.classList.remove(map.activeMap);
  mapContainer.classList.add(ref);
  if (typeof(skipRefresh) === 'undefined' || !skipRefresh) {
    refreshDataLayers();
  }
}

export function displayCoastLine(forceFill, landColor = null) {
  //let coastStyle = ((layers.dataLayers.indexOf('wind_fc') !== -1 || layers.dataLayers.indexOf('waves_fc') !== -1) && !forceFill) ?
    //{stroke: true, color: config.coastColor, fill: false, weight: 2} : {stroke: false, fill: true, fillColor: (landColor || config.landColor), fillOpacity: 1};
  let coastStyle ={};
  if(layers.dataLayers.indexOf('waves_fc') !== -1 && layers.dataLayers.indexOf('wind_fc') !== -1 && !forceFill ){
    coastStyle = {stroke: false, fill: true, fillColor: '#3CA67E', fillOpacity: 1};
  }else if ((layers.dataLayers.indexOf('wind_fc') !== -1 || layers.dataLayers.indexOf('waves_fc') !== -1) && !forceFill ){
    coastStyle = {stroke: true, color: config.coastColor, fill: false, weight: 2};
  }else{
    coastStyle = {stroke: false, fill: true, fillColor: (landColor || config.landColor), fillOpacity: 1};
  }
  return (map.coastLine ? Promise.resolve(map.coastLine) : d3.json(coastJsonUrl)).then(function(coastData) {
    map.coastLine = coastData;
    let geoJson = L.geoJSON(map.coastLine, {style: coastStyle, pane: 'tilePane'});
    geoJson.addTo(map.instance);
    layers.bgLayers.push(geoJson);
    return geoJson;
  });
}

export function clearBgLayers() {
  let l;
  for (l of layers.bgLayers) {
    map.instance.removeLayer(l);
  }
  layers.bgLayers = [];
}

function setPopupContent(popup, evt) {
  console.debug('pt clicked : ' + evt.latlng);
  if (layers.dataLayers.indexOf('wind_fc') !== -1 || layers.dataLayers.indexOf('waves_fc') !== -1) {
    return displayForecastDetails(popup, evt.latlng.lat, evt.latlng.lng);
  } else {
    return popup.setContent(evt.latlng.lat + ', ' + evt.latlng.lng);
  }
}

function displayOrthoLayer() {
  let layr = L.tileLayer("https://tile.geobretagne.fr/gwc02/service/tms/1.0.0/satellite@EPSG%3A3857@jpeg/{z}/{x}/{-y}.jpg");
  layr.addTo(map.instance);
  layers.bgLayers.push(layr);
}

function displayMntLayer() {
  let layr = new L.tileLayer.wms("https://services.data.shom.fr/INSPIRE/wms/r?", {
    layers: 'MNT_COTIER_MORBIHAN_TANDEM_20m_PBMA_3857_WMSR',
    format: 'image/png',
    transparent: true,
    version: '1.3.0',
    service: 'WMS',
    request: 'GetMap',
    uppercase: true,
    className: 'mnt_layer'
  });
  layr.addTo(map.instance);
  layers.bgLayers.push(layr);
}

export function projectInMap(lng, lat) {
  let pt = map.instance.latLngToLayerPoint([lat, lng]);
  return [pt.x, pt.y];
}
